// ⚠️ A SIGNED LINK NEVER GOES IN THE BROWSER CACHE (#1251, 2026-10-23).
//
// The print downloads every original once per render, through a link signed
// for that render — nothing a later render could reuse. Kept in the cache,
// those files fill it: in Cloudflare's robot browser, once 234–429 MB had
// passed, EVERY download failed (net::ERR_FAILED, « Failed to fetch »), the
// photo layer of that page was not drawn, and the print stopped on « photo
// layer not drawn on a themed page » — a different photo each run, because
// the point where the cache gives out moves. #1251 is 51 camera originals,
// 470 MB. Same album, same browser, cache off: 1.4 GB, 0 failures.
//
// Public décor (papers, frames, tapes) stays cached: small files, the same one
// on many pages.
export function renderFetchInit(url: string): RequestInit {
  // The worker's R2 copy of a signed link (below) is the same file: same rule.
  return /\/storage\/v1\/object\/sign\/|\/render-cache\/o\?/.test(url) ? { cache: 'no-store' } : {};
}

// ─── THE WORKER'S R2 COPY (2026-10-29, switch render_photo_cache_enabled) ───
//
// The render downloads every full-size original on every render, and one album
// is often rendered 3-5 times: 487 GB a month of Supabase egress for 34 GB of
// photos. With the switch ticked, the render pages (/_render-spike,
// /_preview-spike, /_quick-spike) send each signed photo / PDF / cover link to
// the worker, which streams a byte-identical copy from R2 (no egress fee) and
// lets Supabase check the link every time (worker lib/renderObjectCache.ts).
//
// ⚠️ OFF IS TODAY, EXACTLY. With no base set (the default, and always in a
// customer's browser) renderFetch is `fetch(url, init)` with the caller's own
// arguments. ON, any refusal or failure of the worker falls back to that same
// direct download — the cache can make a render cheaper, never break one.

const CACHEABLE = /^https:\/\/[^/]+\/storage\/v1\/object\/sign\/(album-photos|pdfs|covers)\//;

let cacheBase: string | null = null;

/** The API base to download through (e.g. `/api`), or null for direct downloads. */
export function setRenderObjectCache(base: string | null): void { cacheBase = base; }
export function renderObjectCacheBase(): string | null { return cacheBase; }

/** The worker cache link for a signed storage link, or null when it is not one. Pure. */
export function renderCacheUrl(url: string, base: string | null): string | null {
  if (!base || !CACHEABLE.test(url)) return null;
  return `${base.replace(/\/$/, '')}/render-cache/o?u=${encodeURIComponent(url)}`;
}

/** A render download: through the worker's R2 copy when on, else (and on any refusal) direct. */
export async function renderFetch(url: string, init?: RequestInit): Promise<Response> {
  const via = renderCacheUrl(url, cacheBase);
  if (via) {
    try {
      const r = await fetch(via, { ...(init ?? {}), cache: 'no-store' });
      if (r.ok) return r;
      await r.body?.cancel().catch(() => {});
    } catch { /* the direct download below, as before */ }
  }
  return fetch(url, init);
}

/** The interior PDF's HEAD, and the URL pdf.js should range-read: the worker's
 *  (whose HEAD fills R2, so every range after is a hit) when on and it answers,
 *  else the signed link itself — exactly today's request. */
export async function renderHeadSource(url: string): Promise<{ res: Response; url: string }> {
  const via = renderCacheUrl(url, cacheBase);
  if (via) {
    try {
      const r = await fetch(via, { method: 'HEAD', cache: 'no-store' });
      if (r.ok) return { res: r, url: via };
    } catch { /* direct, below */ }
  }
  return { res: await fetch(url, { method: 'HEAD' }), url };
}
